import React, {useState, useRef} from 'react';
import {Form} from 'react-bootstrap';

interface FileUploadFieldProps {
  name: string;
  value?: File | File[] | null;
  onChange: (files: File | File[] | null) => void;
  placeholder?: string;
  isRequired?: boolean;
  error?: string;
  touched?: boolean;
  multiple?: boolean;
  accept?: string;
  maxSize?: number; // in bytes
  maxFiles?: number;
  disabled?: boolean;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
    name,
    value,
    onChange,
    placeholder = 'Choose files',
    isRequired,
    error,
    touched,
    multiple = false,
    accept,
    maxSize = 10 * 1024 * 1024, // 10MB default
    maxFiles = multiple ? 5 : 1,
    disabled = false,
}) => {
    const [uploadError, setUploadError] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const hasError = (touched && !!error) || !!uploadError;

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUploadError('');
        const files = Array.from(event.target.files || []);
    
        if (files.length === 0) {
            onChange(null);
            return;
        }

        // Validate file size
        const oversizedFiles = files.filter(file => file.size > maxSize);
        if (oversizedFiles.length > 0) {
            setUploadError(`File size must be less than ${(maxSize / 1024 / 1024).toFixed(1)}MB`);
            return;
        }

        // Validate file count
        if (files.length > maxFiles) {
            setUploadError(`Maximum ${maxFiles} files allowed`);
            return;
        }

        if (multiple) {
            onChange(files);
        } else {
            onChange(files[0]);
        }
    };

    const removeFile = (index?: number) => {
        if (multiple && Array.isArray(value)) {
            if (typeof index === 'number') {
                const newFiles = value.filter((_, i) => i !== index);
                onChange(newFiles.length > 0 ? newFiles : null);
            } else {
                onChange(null);
            }
        } else {
            onChange(null);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) {return '0 Bytes';}
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const renderFileList = () => {
        if (!value) {return null;}

        const files = Array.isArray(value) ? value : [value];
    
        return (
            <div className="mt-2">
                {files.map((file, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center p-2 border rounded mb-1">
                        <div>
                            <small className="fw-bold">{file.name}</small>
                            <br />
                            <small className="text-muted">{formatFileSize(file.size)}</small>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeFile(multiple ? index : undefined)}
                            disabled={disabled}
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div>
            <Form.Control
                ref={fileInputRef}
                type="file"
                name={name}
                onChange={handleFileChange}
                multiple={multiple}
                accept={accept}
                disabled={disabled}
                isInvalid={hasError}
                className="mb-2"
            />
      
            <Form.Text className="text-muted">
                {multiple ? `Up to ${maxFiles} files` : 'Single file'} • 
                Max {(maxSize / 1024 / 1024).toFixed(1)}MB each
                {accept && ` • ${accept}`}
            </Form.Text>

            {renderFileList()}

            {hasError && (
                <Form.Control.Feedback type="invalid" style={{display: 'block'}}>
                    {error || uploadError}
                </Form.Control.Feedback>
            )}

            {isRequired && (
                <Form.Text className="text-muted">
                    <span className="text-danger">*</span> Required
                </Form.Text>
            )}
        </div>
    );
};

export default FileUploadField;
